import React from "react";
import ReactApexChart from "react-apexcharts";

function isSeriesObjects(arr) {
  return Array.isArray(arr) && arr.length > 0 && typeof arr[0] === "object" && "data" in arr[0];
}
function isNumericArray(arr) {
  return Array.isArray(arr) && arr.every(v => typeof v === "number" && Number.isFinite(v));
}
function isStringArray(arr) {
  return Array.isArray(arr) && arr.every(v => typeof v === "string");
}

function normalizeApexData(input) {
  const ds = input ? JSON.parse(JSON.stringify(input)) : {};
  const name = ds.columns_involved_for_series_data || "Series";

  // ——— Prepare options parts (don’t mutate input directly)
  const chart = ds.chart || { type: "bar", height: 350 };
  const dataLabels = ds.dataLabels ?? { enabled: false };
  const plotOptions = ds.plotOptions || { bar: { horizontal: true } };

  let categories = [];
  let seriesOut = [];

  const rawSeries = ds.series;
  const rawXaxis = ds.xaxis;

  // Case 1: Already correct: [{ data: [...] }]
  if (isSeriesObjects(rawSeries)) {
    seriesOut = rawSeries;

    // xaxis can be either {categories: [...]} or an array
    if (rawXaxis && typeof rawXaxis === "object" && Array.isArray(rawXaxis.categories)) {
      categories = rawXaxis.categories;
    } else if (Array.isArray(rawXaxis)) {
      categories = rawXaxis;
    } else {
      categories = [];
    }
  } else {
    // Not objects → try to fix
    const seriesArr = Array.isArray(rawSeries) ? rawSeries : [];
    const xaxisArr = Array.isArray(rawXaxis) ? rawXaxis : [];

    // Case 2: Swapped like your sample → series = labels (strings), xaxis = values (numbers)
    if (isStringArray(seriesArr) && isNumericArray(xaxisArr) && seriesArr.length === xaxisArr.length) {
      categories = seriesArr;
      seriesOut = [{ name, data: xaxisArr }];
    }
    // Case 3: series is numeric → wrap and use xaxis as categories if present
    else if (isNumericArray(seriesArr)) {
      seriesOut = [{ name, data: seriesArr }];
      categories = Array.isArray(xaxisArr) ? xaxisArr : [];
    }
    // Case 4: series is strings and xaxis is also strings/nothing → can’t plot (no numeric values)
    // Try to coerce xaxis into numbers if possible:
    else if (isStringArray(seriesArr) && Array.isArray(xaxisArr)) {
      const coerced = xaxisArr.map(v => (typeof v === "number" ? v : Number(v)));
      if (coerced.every(n => Number.isFinite(n)) && coerced.length === seriesArr.length) {
        categories = seriesArr;
        seriesOut = [{ name, data: coerced }];
      } else {
        // Fallback: empty data to avoid crashes
        categories = seriesArr;
        seriesOut = [{ name, data: new Array(seriesArr.length).fill(0) }];
        console.warn("Apex normalize: no numeric values found; using zeros as fallback.");
      }
    } else {
      // Last resort: build empty safe structures
      categories = [];
      seriesOut = [{ name, data: [] }];
      console.warn("Apex normalize: unsupported data shape; rendering empty chart.");
    }
  }

  // Ensure options.xaxis is an object with categories
  const xaxis =
    ds.xaxis && typeof ds.xaxis === "object" && !Array.isArray(ds.xaxis)
      ? { ...ds.xaxis, categories }
      : { categories };

  return {
    options: {
      chart,
      dataLabels,
      plotOptions,
      xaxis,
    },
    series: seriesOut,
  };
}

const HorizontalBarChart = ({ width, height, data_set }) => {
  const { options, series } = normalizeApexData(data_set);

  return (
    <div>
      <ReactApexChart
        options={options}
        series={series}
        type={(options.chart && options.chart.type) || "bar"}
        height={height || (options.chart && options.chart.height) || 350}
        width={width || "100%"}
      />
    </div>
  );
};

export default HorizontalBarChart;
